import { useState } from 'react';
import { Compass, HelpCircle, PartyPopper, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { useOnboarding } from './useOnboarding';

interface WizardStep {
  icon: typeof Sparkles;
  title: string;
  body: string;
}

/**
 * Pasos del wizard de bienvenida (Anexo E · E6). Texto en voseo, claro,
 * pensado para un empleado que entra por primera vez.
 */
const STEPS: WizardStep[] = [
  {
    icon: Sparkles,
    title: 'Bienvenido/a a DenDev Core',
    body: 'Este es tu lugar de trabajo: desde acá manejás prospectos, campañas, tareas, chat con el equipo y los proyectos. Todo en un solo lugar, sin saltar entre herramientas.',
  },
  {
    icon: Compass,
    title: 'Encontrá todo rápido',
    body: 'A la izquierda tenés el menú con cada sección y una descripción de qué hace. Y si querés ir directo a algo, abrí el buscador con Ctrl + K (o Cmd + K en Mac) y escribí lo que buscás.',
  },
  {
    icon: HelpCircle,
    title: '¿Dudas con un término?',
    body: 'En el menú hay un Centro de ayuda (Ayuda) con un glosario que explica los términos que usamos. Además, los iconos (?) que ves al lado de los campos te aclaran para qué sirve cada uno.',
  },
  {
    icon: PartyPopper,
    title: 'Listo',
    body: 'Eso es todo para arrancar. Cuando quieras repasar algo, volvé a la Ayuda desde el menú. ¡A trabajar!',
  },
];

/**
 * Modal de bienvenida que se muestra UNA sola vez al empleado en su primer
 * ingreso. Se monta en el shell autenticado (AppLayout) para aparecer en
 * cualquier pantalla.
 *
 * - Mientras resuelve el estado o si ya fue descartado → no renderiza nada.
 * - El usuario puede recorrer los pasos o saltarlos; en ambos casos se
 *   marca como visto y no se vuelve a mostrar.
 */
export function WelcomeDialog() {
  const { welcomeDismissed, loading, dismissWelcome } = useOnboarding();
  const [step, setStep] = useState(0);

  if (loading || welcomeDismissed) return null;

  const total = STEPS.length;
  const isFirst = step === 0;
  const isLast = step === total - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  const handleFinish = () => {
    void dismissWelcome();
  };

  return (
    <Dialog
      open
      // No se cierra por click afuera ni con Escape: el empleado decide
      // explícitamente con "Saltar" o "Empezar".
      onOpenChange={(next) => {
        if (!next) handleFinish();
      }}
    >
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <div className="flex justify-center sm:justify-start">
            <span className="inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon className="size-6" aria-hidden="true" />
            </span>
          </div>
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription>{current.body}</DialogDescription>
        </DialogHeader>

        {/* Indicador de progreso · puntitos + "Paso X de N" */}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === step ? 'w-5 bg-primary' : 'w-1.5 bg-muted-foreground/30',
                )}
              />
            ))}
          </div>
          <span className="text-caption text-muted-foreground">
            Paso {step + 1} de {total}
          </span>
        </div>

        <DialogFooter className="sm:items-center sm:justify-between">
          <Button
            variant="link"
            type="button"
            onClick={handleFinish}
            className="px-0 text-muted-foreground hover:text-foreground"
          >
            Saltar
          </Button>

          <div className="flex gap-2">
            {!isFirst && (
              <Button
                variant="outline"
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                Atrás
              </Button>
            )}
            {isLast ? (
              <Button type="button" onClick={handleFinish}>
                Empezar
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              >
                Siguiente
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
